import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Mail, Copy, Check, ChevronDown, ShoppingBag, Wallet,
  Sparkles, Plus, Minus, X, Trash2, ArrowLeft, FileText, Download, Bot,
  ShieldCheck, Compass, FlaskConical, Play, Stethoscope, UtensilsCrossed,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../components/LanguageToggle";
import api, { API, fileUrl, formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { findProductVideo } from "../data/productVideos";
import { findProductFunction } from "../data/productFunctions";
import { findProductDescriptionEs } from "../data/productDescriptionsEs";

const GOLD = "#C9A84C";
const GOLD_DARK = "#8a6a1c";
const GOLD_BG = "#fbf6e7";

// v414.87 — i18n keys (EN/ES): categories + brochure banner labels
const CATEGORY_KEYS = {
  "Precision Nasal Mist": "sf.cat_nasal",
  "Injectable": "sf.cat_injectable",
  "Flagship Topical": "sf.cat_topical",
  "Mushroom Coffee": "sf.cat_coffee",
  "Adaptogens": "sf.cat_adaptogens",
  "Protein": "sf.cat_protein",
  "Supplements": "sf.cat_supplements",
};
const CATEGORY_ORDER = ["Injectable", "Precision Nasal Mist", "Flagship Topical"];
// v414.62 — Everyday Wellness door: these categories live behind the second
// door of the split landing (coffee, botanicals, protein, supplements).
const WELLNESS_ORDER = ["Mushroom Coffee", "Adaptogens", "Protein", "Supplements"];
const BANNER_KEYS = {
  injectables: "sf.banner_injectables",
  nasal: "sf.banner_nasal",
  cream: "sf.banner_cream",
};
const money = (v) => `$${Number(v) % 1 === 0 ? Number(v) : Number(v).toFixed(2)}`;

const copy = async (text, msg, failMsg) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(msg);
  } catch {
    toast.error(failMsg || "Copy failed — long-press to copy manually");
  }
};

// v405.16 — AXIS distributor retail store (reupclub.com/{CODE}).
// Surgical white + gold: full catalog, accordion details, cart, e-transfer
// checkout with AXIS-{DIST}-{YYYYMMDD}-{4} references. Same design for all.
export default function StorePage() {
  const { code } = useParams();
  const { t, i18n } = useTranslation();
  const catLabel = (cat) => (CATEGORY_KEYS[cat] ? t(CATEGORY_KEYS[cat]) : t("sf.cat_more"));
  const [store, setStore] = useState(null);
  const [state, setState] = useState("loading");
  const [cart, setCart] = useState({}); // name -> qty
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  // v414.62 — two-door landing: null = chooser, "bio" | "wellness" = chosen.
  const [door, setDoorState] = useState(null);
  const setDoor = (d) => {
    setDoorState(d);
    try { sessionStorage.setItem(`axis_door_${code}`, d); } catch { /* private mode */ }
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    try { setDoorState(sessionStorage.getItem(`axis_door_${code}`) || null); } catch { setDoorState(null); }
  }, [code]);
  const sectionRefs = useRef({});

  useEffect(() => {
    api.get("/docs").then(({ data }) => setDocs(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/store/${encodeURIComponent(code)}`)
      .then(({ data }) => {
        setStore(data); setState("ok");
        // v414.87 — Mexican stores default to Spanish unless the visitor has
        // explicitly picked a language (reup-lang-user set by the toggle).
        try {
          if (data.currency === "MXN" && !localStorage.getItem("reup-lang-user")
              && !(i18n.resolvedLanguage || "").startsWith("es")) {
            i18n.changeLanguage("es");
          }
        } catch { /* private mode */ }
      })
      .catch(() => setState("notfound"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // v407.7 — structured data for AI assistants / crawlers: per-store title,
  // meta description and a schema.org Product ItemList (JSON-LD).
  useEffect(() => {
    if (!store) return;
    const prevTitle = document.title;
    document.title = `${store.name} — ReUP Store (${store.code})`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    meta?.setAttribute("content",
      `${store.name}'s AXIS store — ${store.count} products. Order online${store.accepts_etransfer === false ? "" : ", pay by Interac e-Transfer"}. $${store.shipping_fee} shipping under $${store.free_shipping_min}, free above.`);
    const ld = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${store.name} — AXIS store product menu`,
      numberOfItems: store.count,
      itemListElement: (store.products || [])
        .filter((p) => p.price != null)
        .map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": "Product",
            name: p.name,
            ...(p.description ? { description: p.description } : {}),
            ...(p.category ? { category: p.category } : {}),
            offers: {
              "@type": "Offer",
              price: Number(p.price).toFixed(2),
              priceCurrency: "CAD",
              availability: "https://schema.org/InStock",
            },
          },
        })),
    };
    let el = document.getElementById("store-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "store-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
    return () => {
      el.remove();
      document.title = prevTitle;
      if (prevDesc != null) meta?.setAttribute("content", prevDesc);
    };
  }, [store]);

  const wellnessCount = useMemo(
    () => (store?.products || []).filter((p) => WELLNESS_ORDER.includes(p.category)).length,
    [store]);
  const bioCount = (store?.products?.length || 0) - wellnessCount;
  // No wellness catalog on this org yet → skip the chooser entirely.
  const effectiveDoor = wellnessCount === 0 ? "bio" : door;

  const groups = useMemo(() => {
    if (!store) return [];
    const isWellness = (p) => WELLNESS_ORDER.includes(p.category);
    if (effectiveDoor === "wellness") {
      return WELLNESS_ORDER
        .map((cat) => ({ cat, items: store.products.filter((p) => p.category === cat) }))
        .filter((g) => g.items.length > 0);
    }
    const prods = store.products.filter((p) => !isWellness(p));
    return CATEGORY_ORDER
      .map((cat) => ({ cat, items: prods.filter((p) => p.category === cat) }))
      .concat([{ cat: "__other", items: prods.filter((p) => !CATEGORY_ORDER.includes(p.category)) }])
      .filter((g) => g.items.length > 0);
  }, [store, effectiveDoor]);

  const cartCount = Object.values(cart).reduce((s, q) => s + q, 0);
  const cartTotal = useMemo(() => {
    if (!store) return 0;
    const priceOf = Object.fromEntries(store.products.map((p) => [p.name, p.price]));
    return Object.entries(cart).reduce((s, [n, q]) => s + (priceOf[n] || 0) * q, 0);
  }, [cart, store]);
  // v406.13 — shipping-aware bar total ($25 under the free threshold)
  const barShipping = store && cartTotal > 0 && cartTotal < (store.free_shipping_min ?? 200)
    ? (store.shipping_fee ?? 25) : 0;
  // v406.14 — coordinator: the store's own distributor or an org admin,
  // signed in on this device, may adjust prices / waive shipping at checkout.
  const { user: authUser, isSuperOwner } = useAuth();
  const coordinator = !!authUser && authUser !== false && !!store && (
    ["owner", "admin", "cfo"].includes(authUser.role) || isSuperOwner ||
    (authUser.distributor_code || "").toUpperCase() === (store.code || "").toUpperCase());

  const setQty = (name, qty) =>
    setCart((c) => {
      if (qty <= 0) { const { [name]: _, ...rest } = c; return rest; }
      return { ...c, [name]: Math.min(qty, 999) };
    });

  if (state === "loading") {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-40">
        <p className="text-[12px] font-semibold uppercase tracking-[0.35em]" style={{ color: GOLD }}>{t("sf.loading")}</p>
      </div>
    );
  }
  if (state === "notfound") {
    return (
      <div className="fixed inset-0 bg-white z-40 flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="store-not-found">
        <p className="text-[26px] font-light tracking-[0.5em] text-zinc-950">AXIS</p>
        <div className="w-10 h-px" style={{ backgroundColor: GOLD }} />
        <p className="text-sm text-zinc-600 max-w-xs">{t("sf.not_active")}</p>
      </div>
    );
  }

  // v414.62 — split landing: the customer picks a door before the catalog.
  if (!effectiveDoor) {
    return (
      <div className="fixed inset-0 overflow-y-auto bg-white z-40" data-testid="store-door-chooser">
        <div className="absolute top-4 right-4"><LanguageToggle langs={["en", "es"]} /></div>
        <div className="min-h-full flex flex-col items-center justify-center px-5 py-14">
          <p className="text-[30px] font-light tracking-[0.5em] text-zinc-950 select-none">AXIS</p>
          <div className="w-10 h-px mt-3 mb-3" style={{ backgroundColor: GOLD }} />
          <p className="text-xl font-bold text-zinc-900" data-testid="store-chooser-name">{store.name}</p>
          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] mt-1.5" style={{ color: GOLD }}>
            {t("sf.choose_experience")}
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 w-full max-w-2xl">
            <button
              type="button"
              onClick={() => setDoor("bio")}
              className="group text-left rounded-2xl p-6 sm:p-8 text-white transition-transform hover:-translate-y-0.5"
              style={{ background: "#101012", border: `1.5px solid ${GOLD}` }}
              data-testid="store-door-bio"
            >
              <FlaskConical size={22} style={{ color: GOLD }} />
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] mt-4" style={{ color: GOLD }}>{t("sf.door_bio_tag")}</p>
              <p className="text-xl font-bold mt-1.5 leading-tight">{t("sf.door_bio_title")}</p>
              <p className="text-[12.5px] text-zinc-400 mt-2 leading-relaxed">
                {t("sf.door_bio_sub")}
              </p>
              <p className="text-[11px] font-semibold mt-4 tabular-nums" style={{ color: GOLD }}>
                {t("sf.products_arrow", { n: bioCount })}
              </p>
            </button>
            <button
              type="button"
              onClick={() => setDoor("wellness")}
              className="group text-left rounded-2xl p-6 sm:p-8 transition-transform hover:-translate-y-0.5 bg-white"
              style={{ border: `1.5px solid ${GOLD}`, background: GOLD_BG }}
              data-testid="store-door-wellness"
            >
              <Sparkles size={22} style={{ color: GOLD_DARK }} />
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] mt-4" style={{ color: GOLD_DARK }}>{t("sf.door_wellness_tag")}</p>
              <p className="text-xl font-bold mt-1.5 leading-tight text-zinc-950">{t("sf.door_wellness_title")}</p>
              <p className="text-[12.5px] text-zinc-600 mt-2 leading-relaxed">
                {t("sf.door_wellness_sub")}
              </p>
              <p className="text-[11px] font-semibold mt-4 tabular-nums" style={{ color: GOLD_DARK }}>
                {t("sf.products_arrow", { n: wellnessCount })}
              </p>
            </button>
          </div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] mt-10" style={{ color: GOLD }}>
            AXIS · {store.code}
          </p>
          {/* v414.66 — owner request: ecosystem box on the store landing too */}
          <div className="mt-8 w-full max-w-2xl">
            <EcosystemBox />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-y-auto bg-white z-40" data-testid="store-page">
      {/* v408.5 — logged-in staff/distributors get a way back to their hub
          (customers never see this) */}
      {authUser && (
        <div className="bg-zinc-950 text-white">
          <Link
            to={(authUser.distributor_code || "").toUpperCase() === (store.code || "").toUpperCase() ? "/my-store" : "/"}
            className="max-w-4xl mx-auto px-5 h-9 flex items-center gap-2 text-[12px] font-semibold tracking-wide hover:text-[#C9A84C] transition-colors"
            data-testid="store-back-to-hub"
          >
            <ArrowLeft size={14} strokeWidth={2.5} /> {t("sf.back_to_hub")}
            <span className="ml-auto text-[10px] font-normal text-zinc-400">{t("sf.viewing_as_customer")}</span>
          </Link>
        </div>
      )}
      {/* Brand bar */}
      <div className="border-b" style={{ borderColor: `${GOLD}55` }}>
        <div className="max-w-4xl mx-auto px-5 h-11 flex items-center justify-between">
          <p className="text-[13px] tracking-[0.35em] font-medium text-zinc-950">
            AXIS <span className="text-[10px] tracking-[0.15em] font-normal text-zinc-400 normal-case">by SmartPeptides.ai</span>
          </p>
          <div className="flex items-center gap-3">
            <LanguageToggle langs={["en", "es"]} />
            <a href={`mailto:${store.contact_email}`} className="inline-flex items-center gap-1.5 text-[12px] font-medium hover:underline" style={{ color: GOLD_DARK }} data-testid="store-contact-email">
              <Mail size={13} /> <span className="hidden sm:inline">{store.contact_email}</span>
            </a>
            <button
              type="button"
              onClick={() => cartCount > 0 && setCheckoutOpen(true)}
              className="relative p-1 text-zinc-800 hover:text-zinc-950 transition-colors"
              aria-label="Cart"
              data-testid="store-header-cart"
            >
              <ShoppingBag size={19} />
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1.5 min-w-[17px] h-[17px] px-0.5 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                  style={{ background: GOLD }}
                  data-testid="store-header-cart-count"
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <header className="max-w-4xl mx-auto px-5 pt-12 pb-8 text-center">
        <p className="text-[30px] font-light tracking-[0.5em] text-zinc-950 select-none">AXIS</p>
        <div className="w-10 h-px mx-auto mt-3 mb-3" style={{ backgroundColor: GOLD }} />
        <p className="text-[11px] font-semibold uppercase tracking-[0.35em]" style={{ color: GOLD }}>
          {effectiveDoor === "wellness" ? t("sf.door_wellness_title") : t("sf.door_bio_tag")}
        </p>
        <div className="mt-6 flex flex-col items-center gap-2">
          <p className="text-xl font-bold text-zinc-900" data-testid="store-distributor-name">{store.name}</p>
        {store.discount_pct > 0 && (
          <span
            className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] text-white"
            style={{ background: GOLD }}
            data-testid="store-discount-badge"
          >
            {t("sf.discount_badge", { pct: store.discount_pct })}
          </span>
        )}
          <button
            type="button"
            onClick={() => copy(store.code, t("sf.code_copied", { code: store.code }), t("sf.copy_failed"))}
            className="inline-flex items-center gap-2 text-[13px] font-mono font-bold tracking-[0.25em] px-3.5 py-1.5 rounded-lg border-2 transition-colors hover:bg-white"
            style={{ color: GOLD_DARK, borderColor: GOLD, background: GOLD_BG }}
            data-testid="store-distributor-code"
          >
            {store.code} <Copy size={13} />
          </button>
        </div>
      </header>

      {/* v414.7 — subtle top banner: brochure downloads + LIA hook */}
      {docs.length > 0 && effectiveDoor !== "wellness" && (
        <div className="max-w-4xl mx-auto px-5 pb-4" data-testid="store-top-banner">
          <div className="rounded-xl border px-4 py-3.5" style={{ borderColor: `${GOLD}55`, background: GOLD_BG }}>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {docs.map((d) => (
                <a
                  key={d.slug}
                  href={`${API}/docs/${d.slug}?dl=1`}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border bg-white text-[11px] font-bold uppercase tracking-[0.12em] transition-colors hover:text-white"
                  style={{ borderColor: GOLD, color: GOLD_DARK }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = GOLD_DARK; }}
                  data-testid={`store-banner-doc-${d.slug}`}
                >
                  <Download size={12} /> {BANNER_KEYS[d.slug] ? t(BANNER_KEYS[d.slug]) : d.title}
                </a>
              ))}
              {/* v414.84 — video guides, front and centre next to the brochures */}
              <Link
                to={`/${encodeURIComponent(code)}/videos`}
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.12em] text-white transition-opacity hover:opacity-90"
                style={{ background: GOLD }}
                data-testid="store-banner-videos"
              >
                <Play size={12} fill="currentColor" /> {t("sf.watch_video_guides")}
              </Link>
            </div>
            <p className="text-center text-[11.5px] text-zinc-600 mt-2.5 leading-relaxed">
              {t("sf.banner_help")}{" "}
              <a
                href="https://app.smartpeptides.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold whitespace-nowrap hover:underline"
                style={{ color: GOLD_DARK }}
                data-testid="store-banner-lia-link"
              >
                <Bot size={12} className="inline -mt-0.5 mr-0.5" />
                {t("sf.ask_lia_free")}
              </a>
            </p>
          </div>
        </div>
      )}

      {/* Section quick-nav */}
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b" style={{ borderColor: `${GOLD}44` }} data-testid="store-quick-nav">
        <div className="max-w-4xl mx-auto px-5 py-2.5 flex gap-2 overflow-x-auto">
          {groups.map(({ cat }) => (
            <button
              key={cat}
              type="button"
              onClick={() => sectionRefs.current[cat]?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="shrink-0 px-3.5 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] border transition-colors hover:text-white"
              style={{ borderColor: GOLD, color: GOLD_DARK }}
              onMouseEnter={(e) => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = GOLD_DARK; }}
              data-testid={`store-nav-${cat}`}
            >
              {catLabel(cat)}
            </button>
          ))}
          {docs.length > 0 && effectiveDoor !== "wellness" && (
            <button
              type="button"
              onClick={() => sectionRefs.current.__docs?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="shrink-0 px-3.5 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] border transition-colors"
              style={{ borderColor: GOLD, color: GOLD_DARK }}
              onMouseEnter={(e) => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = GOLD_DARK; }}
              data-testid="store-nav-guides"
            >
              {t("sf.nav_guides")}
            </button>
          )}
          {/* v414.80 — storefront Video Library */}
          <Link
            to={`/${encodeURIComponent(code)}/videos`}
            className="shrink-0 inline-flex items-center px-3.5 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] border transition-colors"
            style={{ borderColor: GOLD, color: GOLD_DARK }}
            onMouseEnter={(e) => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = GOLD_DARK; }}
            data-testid="store-nav-videos"
          >
            {t("sf.nav_videos")}
          </Link>
          {wellnessCount > 0 && (
            <button
              type="button"
              onClick={() => setDoor(effectiveDoor === "wellness" ? "bio" : "wellness")}
              className="shrink-0 ml-auto px-3.5 h-8 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-opacity hover:opacity-90"
              style={{ background: "#101012", border: `1px solid ${GOLD}` }}
              data-testid="store-door-switch"
            >
              ⇄ {effectiveDoor === "wellness" ? t("sf.switch_to_bio") : t("sf.door_wellness_title")}
            </button>
          )}
        </div>
      </nav>

      {/* Catalog */}
      <main className="max-w-4xl mx-auto px-5 py-10 space-y-14" style={{ paddingBottom: cartCount > 0 ? 120 : 64 }}>
        {groups.map(({ cat, items }) => (
          <section key={cat} ref={(el) => { sectionRefs.current[cat] = el; }} className="scroll-mt-16" data-testid={`store-section-${cat}`}>
            <div className="flex items-center gap-3 mb-5">
              <Sparkles size={16} style={{ color: GOLD }} />
              <h2 className="text-lg font-bold text-zinc-950 tracking-tight">{catLabel(cat)}</h2>
              <div className="flex-1 h-px" style={{ backgroundColor: `${GOLD}55` }} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {items.map((p) => (
                <ProductCard key={p.name} p={p} qty={cart[p.name] || 0} setQty={setQty} storeCode={code} />
              ))}
            </div>
          </section>
        ))}
        {/* v414.5 — downloadable product guides + SmartPeptides / LIA promo */}
        {docs.length > 0 && effectiveDoor !== "wellness" && (
          <section ref={(el) => { sectionRefs.current.__docs = el; }} className="scroll-mt-16" data-testid="store-docs-section">
            <div className="flex items-center gap-3 mb-5">
              <FileText size={16} style={{ color: GOLD }} />
              <h2 className="text-lg font-bold text-zinc-950 tracking-tight">{t("sf.guides_documents")}</h2>
              <div className="flex-1 h-px" style={{ backgroundColor: `${GOLD}55` }} />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {docs.map((d) => (
                <div key={d.slug} className="rounded-xl border bg-white p-4 flex flex-col" style={{ borderColor: `${GOLD}66` }} data-testid={`store-doc-${d.slug}`}>
                  <FileText size={18} style={{ color: GOLD_DARK }} />
                  <p className="text-sm font-bold text-zinc-900 mt-2 leading-snug">{d.title}</p>
                  <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed flex-1">{d.blurb}</p>
                  <a
                    href={`${API}/docs/${d.slug}?dl=1`}
                    className="mt-3 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-opacity hover:opacity-90"
                    style={{ background: GOLD }}
                    data-testid={`store-doc-download-${d.slug}`}
                  >
                    <Download size={13} /> {t("sf.download_pdf")}
                  </a>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl p-5 sm:p-7 text-white" style={{ background: "#101012", border: `1px solid ${GOLD}` }} data-testid="store-lia-panel">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: GOLD }}>SmartPeptides.ai</p>
                  <p className="text-lg font-bold mt-1.5 leading-snug">
                    {t("sf.lia_meet")}
                  </p>
                  <p className="text-[12px] text-zinc-400 mt-1 leading-relaxed">
                    {t("sf.lia_free_a")} <span className="font-bold" style={{ color: GOLD }}>{t("sf.lia_free_word")}</span>{t("sf.lia_free_b")}
                  </p>
                </div>
                <a
                  href="https://app.smartpeptides.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 inline-flex items-center justify-center gap-2 px-5 h-11 rounded-lg text-[12px] font-bold uppercase tracking-[0.2em] text-black transition-opacity hover:opacity-90"
                  style={{ background: GOLD }}
                  data-testid="store-lia-link"
                >
                  <Bot size={16} /> {t("sf.ask_lia")}
                </a>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg p-3.5" style={{ background: "#1a1a1e", border: `1px solid ${GOLD}33` }} data-testid="store-lia-feature-conflicts">
                  <ShieldCheck size={17} style={{ color: GOLD }} />
                  <p className="text-[13px] font-bold mt-2">{t("sf.lia_conflicts_t")}</p>
                  <p className="text-[11.5px] text-zinc-400 mt-1 leading-relaxed">
                    {t("sf.lia_conflicts_b")}
                  </p>
                </div>
                <div className="rounded-lg p-3.5" style={{ background: "#1a1a1e", border: `1px solid ${GOLD}33` }} data-testid="store-lia-feature-nextsteps">
                  <Compass size={17} style={{ color: GOLD }} />
                  <p className="text-[13px] font-bold mt-2">{t("sf.lia_next_t")}</p>
                  <p className="text-[11.5px] text-zinc-400 mt-1 leading-relaxed">
                    {t("sf.lia_next_b")}
                  </p>
                </div>
                <div className="rounded-lg p-3.5" style={{ background: "#1a1a1e", border: `1px solid ${GOLD}33` }} data-testid="store-lia-feature-research">
                  <FlaskConical size={17} style={{ color: GOLD }} />
                  <p className="text-[13px] font-bold mt-2">{t("sf.lia_science_t")}</p>
                  <p className="text-[11.5px] text-zinc-400 mt-1 leading-relaxed">
                    {t("sf.lia_science_b")}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}
        {/* v414.64 — ecosystem box: every app in the family, on every door */}
        <EcosystemBox />
        <footer className="text-center space-y-1 pt-2">
          <p className="text-[11px] text-zinc-400" data-testid="store-currency-note">
            {t("sf.prices_in", { cur: store.currency || "CAD" })}{store.accepts_etransfer === false ? "" : ` ${t("sf.etransfer_note")}`}
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em]" style={{ color: GOLD }}>AXIS · {store.code}</p>
        </footer>
      </main>

      {/* Floating cart bar */}
      {cartCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 p-3" data-testid="store-cart-bar">
          <div className="max-w-4xl mx-auto space-y-1.5">
            {barShipping > 0 && (
              <div className="text-center">
                <span className="inline-block text-[11px] font-bold px-3 py-1 rounded-full shadow-md" style={{ background: GOLD, color: "#fff" }} data-testid="grid-free-shipping-nudge">
                  {t("sf.away_free", { amt: money(Math.max(0, (store.free_shipping_min ?? 200) - cartTotal)) })}
                </span>
              </div>
            )}
            <div className="bg-white border-2 rounded-2xl shadow-xl px-3 py-3 flex items-center gap-2" style={{ borderColor: GOLD }}>
            <button
              type="button"
              onClick={() => setCheckoutOpen(true)}
              className="flex items-center gap-3 flex-1 min-w-0 text-left"
              data-testid="store-cart-bar-summary"
            >
              <ShoppingBag size={20} className="shrink-0" style={{ color: GOLD_DARK }} />
              <p className="text-sm font-bold text-zinc-900 truncate">
                {t("sf.cart_items", { count: cartCount })} <span className="font-semibold" style={{ color: GOLD_DARK }}>· {money(cartTotal + barShipping)}</span>
                {barShipping > 0
                  ? <span className="block text-[10px] font-medium text-zinc-400">{t("sf.incl_shipping", { fee: money(barShipping), min: money(store.free_shipping_min ?? 200) })}</span>
                  : <span className="block text-[10px] font-bold text-emerald-600">{t("sf.free_shipping")}</span>}
              </p>
            </button>
            <button
              type="button"
              onClick={() => setCheckoutOpen(true)}
              className="h-11 px-5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 shrink-0"
              style={{ background: GOLD }}
              data-testid="store-review-order-btn"
            >
              {t("sf.review_order")}
            </button>
            <button
              type="button"
              onClick={() => { setCart({}); toast.success(t("sf.order_cleared")); }}
              className="w-11 h-11 rounded-xl border border-zinc-200 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:border-red-200 transition-colors shrink-0"
              aria-label={t("sf.clear_order")}
              title={t("sf.clear_order")}
              data-testid="store-cart-clear-btn"
            >
              <X size={18} />
            </button>
            </div>
          </div>
        </div>
      )}

      {checkoutOpen && (
        <CheckoutModal
          store={store}
          cart={cart}
          setQty={setQty}
          total={cartTotal}
          coordinator={coordinator}
          onClose={() => setCheckoutOpen(false)}
          onPlaced={() => setCart({})}
        />
      )}
    </div>
  );
}

// v414.64/66 — ecosystem box: shared by the door-chooser landing and both
// catalog doors. Every app in the family, with how each closes the loop.
function EcosystemBox() {
  const { t } = useTranslation();
  const apps = [
    {
      testId: "store-eco-smartpeptides",
      href: "https://app.smartpeptides.ai",
      icon: <Compass size={17} style={{ color: GOLD }} />,
      iconStyle: { background: "#000", border: `1px solid ${GOLD}` },
      name: (<>LIA <span className="font-light text-zinc-500">|</span> SmartPeptides</>),
      copy: t("sf.eco_lia"),
      link: "app.smartpeptides.ai", linkColor: GOLD,
    },
    {
      testId: "store-eco-smartplaite",
      href: "https://app.smartplaite.com",
      icon: <UtensilsCrossed size={17} style={{ color: "#a78bfa" }} />,
      iconStyle: { background: "#2a2040", border: "1px solid #8b72e0" },
      name: "Smartplaite",
      copy: t("sf.eco_plaite"),
      link: "app.smartplaite.com", linkColor: "#a78bfa",
    },
    {
      testId: "store-eco-tapmd",
      href: "https://tapmd.ca",
      icon: <Stethoscope size={17} style={{ color: "#5eead4" }} />,
      iconStyle: { background: "#0f2e2a", border: "1px solid #3fbfa8" },
      name: "tapMD",
      copy: t("sf.eco_tapmd"),
      link: "tapmd.ca", linkColor: "#5eead4",
    },
  ];
  return (
    <section className="rounded-2xl p-6 sm:p-8 text-white" style={{ background: "#101012", border: `1.5px solid ${GOLD}` }} data-testid="store-ecosystem">
      <p className="text-[10px] font-bold uppercase tracking-[0.35em]" style={{ color: GOLD }}>{t("sf.eco_tag")}</p>
      <h2 className="text-xl sm:text-2xl font-bold mt-1.5">{t("sf.eco_title")}</h2>
      <p className="text-[12.5px] text-zinc-400 mt-2 leading-relaxed max-w-xl">
        {t("sf.eco_sub")}
      </p>
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {apps.map((a) => (
          <a
            key={a.testId}
            href={a.href}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg p-4 block transition-colors hover:bg-[#202024]"
            style={{ background: "#1a1a1e", border: `1px solid ${GOLD}33` }}
            data-testid={a.testId}
          >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg" style={a.iconStyle}>{a.icon}</span>
            <p className="text-[13.5px] font-bold mt-2.5">{a.name}</p>
            <p className="text-[11.5px] text-zinc-400 mt-1 leading-relaxed">{a.copy}</p>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.15em] mt-2.5" style={{ color: a.linkColor }}>{a.link} →</p>
          </a>
        ))}
      </div>
    </section>
  );
}

function ProductCard({ p, qty, setQty, storeCode }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // v414.18 — inline explainer video (EN/RU/FR) on cards with a matching clip.
  const video = useMemo(() => findProductVideo(p.name, p.category), [p.name, p.category]);
  // v414.89 — Spanish catalog copy: tagline + description swap on es locale.
  const isEs = (i18n.resolvedLanguage || "").startsWith("es");
  const fn = useMemo(() => findProductFunction(p.name, isEs ? "es" : "en"), [p.name, isEs]);
  const desc = (isEs && findProductDescriptionEs(p.name)) || p.description;
  const [showVideo, setShowVideo] = useState(false);
  const [vlang, setVlang] = useState("en");
  // v414.25 — play tracking: one count per card+language per visit.
  const playedRef = useRef(new Set());
  const trackPlay = () => {
    if (!video || playedRef.current.has(vlang)) return;
    playedRef.current.add(vlang);
    api.post(`/store/${storeCode}/video-view`, { video_name: video.name, lang: vlang }).catch(() => {});
  };
  return (
    <div className="bg-white border rounded-2xl p-4 flex flex-col gap-3 transition-shadow hover:shadow-md" style={{ borderColor: qty > 0 ? GOLD : "#e4e4e7" }} data-testid="store-product-row">
      <div className="flex items-start gap-3">
        {p.photo_path ? (
          <img src={fileUrl(p.photo_path)} alt="" loading="lazy" className="w-16 h-16 rounded-xl object-cover border border-zinc-200 shrink-0" data-testid="store-product-photo" />
        ) : (
          <div className="w-12 h-12 rounded-xl shrink-0 border flex items-center justify-center" style={{ borderColor: `${GOLD}44`, background: GOLD_BG }}>
            <span className="text-[10px] font-light tracking-widest" style={{ color: GOLD }}>AX</span>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold text-zinc-950 leading-snug">{p.name}</p>
          {fn && (
            <p className="text-[11px] font-semibold leading-snug mt-0.5" style={{ color: GOLD_DARK }} data-testid="store-product-function">{fn}</p>
          )}
          <p className="text-[11px] text-zinc-400 uppercase tracking-wide mt-0.5">{t("sf.per_unit", { unit: p.unit?.replace(/s$/, "") || "unit" })}</p>
        </div>
        <div className="text-right shrink-0">
          {p.coming_soon ? (
            <span
              className="inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em]"
              style={{ background: GOLD_BG, color: GOLD_DARK, border: `1px solid ${GOLD}` }}
              data-testid="store-coming-soon-badge"
            >
              {t("sf.coming_soon")}
            </span>
          ) : (
            <>
              {p.compare_at != null && (
                <p className="text-[11px] text-zinc-400 line-through tabular-nums" data-testid="store-compare-at">{money(p.compare_at)}</p>
              )}
              <p className="text-[16px] font-bold tabular-nums" style={{ color: GOLD_DARK }}>
                {p.price != null ? money(p.price) : t("sf.price_ask")}
              </p>
            </>
          )}
        </div>
      </div>

      {desc && (
        <div>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-zinc-500 hover:text-zinc-800 transition-colors"
            data-testid="store-details-toggle"
          >
            {t("sf.details")} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && <p className="text-[13px] text-zinc-600 leading-relaxed mt-2">{desc}</p>}
        </div>
      )}

      {video && (
        <div>
          <button
            type="button"
            onClick={() => setShowVideo(!showVideo)}
            className="inline-flex items-center gap-1.5 text-[12px] font-bold transition-opacity hover:opacity-80"
            style={{ color: GOLD_DARK }}
            data-testid="store-video-toggle"
          >
            <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: GOLD_BG, border: `1px solid ${GOLD}` }}>
              <Play size={10} fill={GOLD_DARK} />
            </span>
            {t("sf.watch_video")}
            <ChevronDown size={14} className={`transition-transform ${showVideo ? "rotate-180" : ""}`} />
          </button>
          {showVideo && (
            <div className="mt-2 space-y-1.5" data-testid="store-video-panel">
              <div className="grid grid-cols-3 gap-1">
                {["en", "ru", "fr"].map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setVlang(l)}
                    data-testid={`store-video-lang-${l}`}
                    className="h-7 rounded-lg border text-[11px] font-bold uppercase tracking-wide transition-colors"
                    style={vlang === l
                      ? { background: "#101012", color: GOLD, borderColor: GOLD }
                      : { background: "#fff", color: "#71717a", borderColor: "#e4e4e7" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <video
                key={video[vlang]}
                src={video[vlang]}
                controls
                playsInline
                preload="none"
                onPlay={trackPlay}
                className="w-full aspect-[9/16] rounded-xl bg-zinc-950 object-contain"
                data-testid="store-video-player"
              />
            </div>
          )}
        </div>
      )}

      <div className="mt-auto">
        {p.coming_soon ? (
          <div
            className="w-full h-11 rounded-xl text-sm font-bold flex items-center justify-center"
            style={{ background: GOLD_BG, color: GOLD_DARK, border: `1px dashed ${GOLD}` }}
            data-testid="store-coming-soon-cta"
          >
            {t("sf.coming_soon_cta")}
          </div>
        ) : qty === 0 ? (
          <button
            type="button"
            onClick={() => setQty(p.name, 1)}
            disabled={p.price == null}
            className="w-full h-11 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
            style={{ background: GOLD }}
            data-testid="store-add-btn"
          >
            {t("sf.add_to_order")}
          </button>
        ) : (
          <div className="flex items-center justify-between h-11 rounded-xl border-2 px-2" style={{ borderColor: GOLD, background: GOLD_BG }} data-testid="store-qty-stepper">
            <button type="button" onClick={() => setQty(p.name, qty - 1)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white transition-colors" style={{ color: GOLD_DARK }} data-testid="store-qty-minus">
              <Minus size={16} />
            </button>
            <span className="text-sm font-bold tabular-nums" style={{ color: GOLD_DARK }} data-testid="store-qty-value">{qty}</span>
            <button type="button" onClick={() => setQty(p.name, qty + 1)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white transition-colors" style={{ color: GOLD_DARK }} data-testid="store-qty-plus">
              <Plus size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// v406.12 — remember the customer on this device so repeat checkout is instant.
const CUSTOMER_KEY = "axis_customer";
const loadSavedCustomer = () => {
  try { return JSON.parse(localStorage.getItem(CUSTOMER_KEY)) || {}; } catch { return {}; }
};

function CheckoutModal({ store, cart, setQty, total, onClose, onPlaced, coordinator }) {
  const { t } = useTranslation();
  const saved = loadSavedCustomer();
  const [name, setName] = useState(saved.name || "");
  const [email, setEmail] = useState(saved.email || "");
  const [contact, setContact] = useState(saved.contact || "");
  const [address, setAddress] = useState(saved.address || "");
  const intlStore = store.accepts_etransfer === false; // v413.2 — no Interac abroad
  const [payment, setPayment] = useState(intlStore ? "arrange" : "etransfer");
  // v412.2 — customer delivery choice: ship (fee rules apply) or local pickup.
  const [delivery, setDelivery] = useState("ship");
  const [placing, setPlacing] = useState(false);
  const [result, setResult] = useState(null);
  // v406.14 — coordinator controls (store's distributor / org admin only)
  const [priceEdits, setPriceEdits] = useState({}); // name -> input string
  const [waive, setWaive] = useState(false);
  const priceOf = Object.fromEntries(store.products.map((p) => [p.name, p.price]));
  const lines = Object.entries(cart);
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email.trim());
  const effPrice = (n) => {
    if (coordinator && priceEdits[n] !== undefined && priceEdits[n] !== "" && !isNaN(Number(priceEdits[n]))) {
      return Math.max(0, Number(priceEdits[n]));
    }
    return priceOf[n] || 0;
  };
  const effSubtotal = lines.reduce((s, [n, q]) => s + effPrice(n) * q, 0);
  // v406.13 — shipping: flat fee under the free-shipping threshold.
  // v412.2 — no fee at all for local pickup.
  const freeMin = store.free_shipping_min ?? 200;
  const feeAmount = store.shipping_fee ?? 25;
  const isPickup = delivery === "pickup";
  const baseShipping = !isPickup && effSubtotal > 0 && effSubtotal < freeMin ? feeAmount : 0;
  // v413.0 — one-time discount code
  const [dcInput, setDcInput] = useState("");
  const [dcApplied, setDcApplied] = useState(null); // {code, kind, value}
  const [dcChecking, setDcChecking] = useState(false);
  const round2 = (v) => Math.round(v * 100) / 100;
  const discountAmt = dcApplied
    ? round2(dcApplied.kind === "percent"
        ? effSubtotal * dcApplied.value / 100
        : Math.min(dcApplied.value, effSubtotal))
    : 0;
  const shippingWaived = coordinator && waive && baseShipping > 0;
  const shipping = shippingWaived ? 0 : baseShipping;
  // v414.33 — optional concierge add-on: $5/vial, pickup only, store opt-in.
  const [concierge, setConcierge] = useState(false);
  const perVial = store.concierge_per_vial ?? 5;
  const vialCount = lines.reduce((s, [n, q]) => {
    const u = (store.products.find((p) => p.name === n)?.unit || "").toLowerCase();
    return s + (u.includes("vial") ? q : 0);
  }, 0);
  const conciergeAvailable = !!store.concierge_enabled && vialCount > 0;
  const conciergeFee = conciergeAvailable && isPickup && concierge
    ? round2(perVial * vialCount) : 0;
  const grandTotal = round2(effSubtotal - discountAmt + shipping + conciergeFee);
  const awayFromFree = Math.max(0, freeMin - effSubtotal);

  const applyDiscount = async () => {
    const code = dcInput.trim().toUpperCase();
    if (!code) return;
    setDcChecking(true);
    try {
      const { data } = await api.get(
        `/store/${encodeURIComponent(store.code)}/discount/${encodeURIComponent(code)}`);
      setDcApplied({ code: data.code, kind: data.kind, value: data.value });
      toast.success(t("sf.code_applied", { code: data.code }));
    } catch (e) {
      setDcApplied(null);
      toast.error(formatApiError(e));
    } finally {
      setDcChecking(false);
    }
  };

  const placeOrder = async () => {
    if (!name.trim()) { toast.error(t("sf.err_name")); return; }
    if (payment === "etransfer" && !emailValid) {
      toast.error(t("sf.err_email_receipt"));
      return;
    }
    if (email.trim() && !emailValid) { toast.error(t("sf.err_email")); return; }
    if (payment === "etransfer" && !isPickup && !address.trim()) {
      toast.error(t("sf.err_address"));
      return;
    }
    setPlacing(true);
    try {
      const overridesOut = {};
      if (coordinator) {
        for (const [n] of lines) {
          const p = effPrice(n);
          if (p !== (priceOf[n] || 0)) overridesOut[n] = p;
        }
      }
      const { data } = await api.post(`/store/${encodeURIComponent(store.code)}/orders`, {
        customer_name: name.trim(),
        customer_email: email.trim() || null,
        customer_contact: contact.trim() || null,
        shipping_address: address.trim() || null,
        payment_method: payment,
        delivery_method: delivery,
        items: lines.map(([n, q]) => ({ name: n, quantity: q })),
        ...(dcApplied ? { discount_code: dcApplied.code } : {}),
        ...(conciergeFee > 0 ? { concierge: true } : {}),
        ...(Object.keys(overridesOut).length ? { price_overrides: overridesOut } : {}),
        ...(shippingWaived ? { waive_shipping: true } : {}),
      });
      setResult({ ...data, customer_email: email.trim() || null });
      try {
        localStorage.setItem(CUSTOMER_KEY, JSON.stringify(
          { name: name.trim(), email: email.trim(), contact: contact.trim(), address: address.trim() }));
      } catch { /* private mode — ignore */ }
      onPlaced();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("sf.err_order");
      toast.error(msg);
      if (/discount/i.test(String(msg))) setDcApplied(null);
    } finally {
      setPlacing(false);
    }
  };

  const mailtoHref = result
    ? `mailto:${result.etransfer_email}?subject=${encodeURIComponent(`Order ${result.reference}`)}&body=${encodeURIComponent(
        `Order reference: ${result.reference}\n\n${result.items.map((i) => `${i.name} x ${i.quantity} — $${i.subtotal.toFixed(2)}`).join("\n")}\n${result.shipping_fee ? `Shipping: $${result.shipping_fee.toFixed(2)}\n` : ""}${result.concierge_fee ? `Concierge service: $${result.concierge_fee.toFixed(2)}\n` : ""}\nTotal: $${result.total.toFixed(2)}\nName: ${name}`)}`
    : "#";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[94vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="store-checkout-modal"
      >
        {!result ? (
          lines.length === 0 ? (
            /* v406.10 — graceful empty state once the last item is removed */
            <div className="p-8 text-center space-y-4" data-testid="checkout-empty-state">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center" style={{ background: GOLD_BG, border: `2px solid ${GOLD}` }}>
                <ShoppingBag size={24} style={{ color: GOLD_DARK }} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-zinc-950">{t("sf.co_empty_t")}</h2>
                <p className="text-[13px] text-zinc-500 mt-1">{t("sf.co_empty_b")}</p>
              </div>
              <button type="button" onClick={onClose}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
                style={{ background: GOLD }}
                data-testid="checkout-empty-close-btn">
                {t("sf.co_back")}
              </button>
            </div>
          ) : (
          <div className="p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-zinc-950">{t("sf.co_title")}</h2>
                <p className="text-[12px] text-zinc-500 mt-0.5">{store.name} · {store.code}</p>
              </div>
              <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="store-checkout-close"><X size={20} /></button>
            </div>

            {coordinator && (
              <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2" data-testid="coordinator-banner">
                <p className="text-[11px] font-semibold text-violet-700">{t("sf.coord_mode")}</p>
                <p className="text-[11px] text-violet-600">{t("sf.coord_note")}</p>
              </div>
            )}

            {/* Lines */}
            <div className="divide-y divide-zinc-100 border-y border-zinc-100">
              {lines.map(([n, q]) => (
                <div key={n} className="py-2.5 flex items-center gap-2" data-testid="checkout-line">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{n}</p>
                    {coordinator ? (
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[11px] text-zinc-400">$</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={priceEdits[n] !== undefined ? priceEdits[n] : String(priceOf[n] ?? "")}
                          onChange={(e) => setPriceEdits({ ...priceEdits, [n]: e.target.value })}
                          className={`w-20 h-7 px-1.5 rounded-md border text-[12px] font-semibold tabular-nums focus:outline-none focus:border-violet-400 ${effPrice(n) !== (priceOf[n] || 0) ? "border-violet-300 bg-violet-50 text-violet-800" : "border-zinc-200 text-zinc-700"}`}
                          data-testid={`coordinator-price-${n}`}
                        />
                        <span className="text-[11px] text-zinc-400">{t("sf.each")}</span>
                        {effPrice(n) !== (priceOf[n] || 0) && (
                          <span className="text-[10px] text-zinc-400 line-through">{money(priceOf[n] || 0)}</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-[11px] text-zinc-400">{t("sf.each_price", { price: money(priceOf[n] || 0) })}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => setQty(n, q - 1)} className="w-7 h-7 rounded-md border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-zinc-400"><Minus size={13} /></button>
                    <span className="w-6 text-center text-sm font-bold tabular-nums">{q}</span>
                    <button type="button" onClick={() => setQty(n, q + 1)} className="w-7 h-7 rounded-md border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-zinc-400"><Plus size={13} /></button>
                    <button type="button" onClick={() => setQty(n, 0)} className="w-7 h-7 flex items-center justify-center text-zinc-300 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                  <p className="w-16 text-right text-sm font-bold tabular-nums" style={{ color: GOLD_DARK }}>{money(effPrice(n) * q)}</p>
                </div>
              ))}
            </div>
            {/* v412.2 — delivery choice: ship (fee rules) vs local pickup (no fee) */}
            <div className="grid grid-cols-2 gap-2.5">
              <button type="button" onClick={() => setDelivery("ship")}
                className="rounded-xl px-3 py-2.5 text-left border-2 transition-colors"
                style={delivery === "ship" ? { background: GOLD_BG, borderColor: GOLD, color: "#18181b" } : { background: "#fff", borderColor: "#e4e4e7", color: "#18181b" }}
                data-testid="checkout-delivery-ship">
                <p className="text-[13px] font-bold leading-tight">{t("sf.ship_to_me")}</p>
                <p className="text-[10px] mt-0.5 text-zinc-400">{t("sf.free_at", { min: money(freeMin) })}</p>
              </button>
              <button type="button" onClick={() => setDelivery("pickup")}
                className="rounded-xl px-3 py-2.5 text-left border-2 transition-colors"
                style={delivery === "pickup" ? { background: GOLD_BG, borderColor: GOLD, color: "#18181b" } : { background: "#fff", borderColor: "#e4e4e7", color: "#18181b" }}
                data-testid="checkout-delivery-pickup">
                <p className="text-[13px] font-bold leading-tight">{t("sf.pickup")}</p>
                <p className="text-[10px] mt-0.5 text-zinc-400">{t("sf.no_ship_fee")}</p>
              </button>
            </div>

            {/* v414.33 — optional concierge add-on (pickup only, store opt-in) */}
            {conciergeAvailable && (
              isPickup ? (
                <label className="flex items-start gap-2.5 rounded-xl border-2 px-3 py-2.5 cursor-pointer select-none transition-colors"
                  style={concierge ? { background: GOLD_BG, borderColor: GOLD } : { background: "#fff", borderColor: "#e4e4e7" }}
                  data-testid="checkout-concierge-option">
                  <input type="checkbox" checked={concierge} onChange={(e) => setConcierge(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#C9A84C]" data-testid="checkout-concierge-checkbox" />
                  <span>
                    <span className="block text-[13px] font-bold text-zinc-900">
                      {t("sf.concierge_add", { per: money(perVial) })}
                      {vialCount > 0 && <span style={{ color: GOLD_DARK }}> (+{money(perVial * vialCount)})</span>}
                    </span>
                    <span className="block text-[11px] text-zinc-500 mt-0.5">
                      {t("sf.concierge_opt")}
                    </span>
                  </span>
                </label>
              ) : (
                <p className="text-[11px] font-medium text-zinc-500 rounded-lg bg-zinc-50 border border-zinc-200 px-2.5 py-1.5" data-testid="checkout-concierge-ship-note">
                  {t("sf.concierge_ship_note")}
                </p>
              )
            )}

            {/* v413.0 — discount code */}
            <div>
              {dcApplied ? (
                <div className="flex items-center gap-2 rounded-xl border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5" data-testid="checkout-discount-applied">
                  <span className="text-sm font-bold text-emerald-800 font-mono">{dcApplied.code}</span>
                  <span className="text-xs font-semibold text-emerald-700">
                    {dcApplied.kind === "percent" ? t("sf.pct_off", { v: dcApplied.value }) : t("sf.amt_off", { v: dcApplied.value })}
                  </span>
                  <span className="flex-1" />
                  <button type="button" onClick={() => { setDcApplied(null); setDcInput(""); }}
                    className="text-xs font-bold text-emerald-700 underline underline-offset-2"
                    data-testid="checkout-discount-remove">
                    {t("sf.remove")}
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={dcInput}
                    onChange={(e) => setDcInput(e.target.value.toUpperCase())}
                    placeholder={t("sf.discount_ph")}
                    className="flex-1 min-w-0 px-4 py-2.5 rounded-xl border border-zinc-300 text-sm font-mono font-semibold uppercase focus:outline-none focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20"
                    data-testid="checkout-discount-input"
                  />
                  <button type="button" onClick={applyDiscount} disabled={dcChecking || !dcInput.trim()}
                    className="shrink-0 px-4 rounded-xl border-2 text-sm font-bold disabled:opacity-40"
                    style={{ borderColor: GOLD, color: "#18181b", background: GOLD_BG }}
                    data-testid="checkout-discount-apply">
                    {dcChecking ? "…" : t("sf.apply")}
                  </button>
                </div>
              )}
            </div>

            {/* Totals w/ shipping */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-[13px] text-zinc-500">
                <span>{t("sf.subtotal")}</span><span className="tabular-nums" data-testid="checkout-subtotal">{money(effSubtotal)}</span>
              </div>
              {discountAmt > 0 && (
                <div className="flex justify-between text-[13px] font-bold text-emerald-700">
                  <span>{t("sf.discount_lbl", { code: dcApplied.code })}</span>
                  <span className="tabular-nums" data-testid="checkout-discount-amount">−{money(discountAmt)}</span>
                </div>
              )}
              <div className="flex justify-between text-[13px] text-zinc-500">
                <span>{t("sf.shipping")}</span>
                {isPickup ? (
                  <span className="font-bold text-emerald-600" data-testid="checkout-shipping">{t("sf.pickup_none")}</span>
                ) : shippingWaived ? (
                  <span className="font-bold text-violet-600" data-testid="checkout-shipping">{t("sf.waived")} <span className="text-zinc-400 line-through font-normal">{money(baseShipping)}</span></span>
                ) : shipping > 0 ? (
                  <span className="tabular-nums" data-testid="checkout-shipping">{money(shipping)}</span>
                ) : (
                  <span className="font-bold text-emerald-600" data-testid="checkout-shipping">{t("sf.free")}</span>
                )}
              </div>
              {coordinator && baseShipping > 0 && (
                <label className="flex items-center gap-2 text-[12px] font-medium text-violet-700 cursor-pointer select-none">
                  <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)}
                    className="accent-violet-600 w-4 h-4" data-testid="coordinator-waive-shipping" />
                  {t("sf.waive_shipping")}
                </label>
              )}
              {shipping > 0 && !coordinator && !isPickup && (
                <p className="text-[11px] font-medium rounded-lg px-2.5 py-1.5" style={{ background: GOLD_BG, color: GOLD_DARK }} data-testid="checkout-free-shipping-nudge">
                  {t("sf.add_more_free", { amt: money(awayFromFree) })}
                </p>
              )}
              {conciergeFee > 0 && (
                <div className="flex justify-between text-[13px] text-zinc-500">
                  <span>{t("sf.concierge_fee_lbl", { n: vialCount, per: money(perVial) })}</span>
                  <span className="tabular-nums" data-testid="checkout-concierge-fee">{money(conciergeFee)}</span>
                </div>
              )}
              <div className="flex justify-between text-[15px] font-bold text-zinc-950 pt-1 border-t border-zinc-100">
                <span>{t("sf.total")}</span><span style={{ color: GOLD_DARK }} data-testid="checkout-total">{money(grandTotal)}</span>
              </div>
            </div>

            {/* Customer */}
            <div className="space-y-2.5">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("sf.name_ph")}
                className="w-full h-12 px-4 rounded-xl border border-zinc-300 text-sm font-medium focus:outline-none focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20"
                data-testid="checkout-name-input" />
              <div>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" inputMode="email" autoComplete="email"
                  placeholder={payment === "etransfer" ? t("sf.email_ph_receipt") : t("sf.email_ph_opt")}
                  className="w-full h-12 px-4 rounded-xl border border-zinc-300 text-sm font-medium focus:outline-none focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20"
                  data-testid="checkout-email-input" />
                {payment === "etransfer" && (
                  <p className="text-[11px] text-zinc-400 mt-1 px-1">{t("sf.receipt_note")}</p>
                )}
              </div>
              <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder={t("sf.phone_ph")}
                className="w-full h-12 px-4 rounded-xl border border-zinc-300 text-sm font-medium focus:outline-none focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20"
                data-testid="checkout-contact-input" />
              <div>
                <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2}
                  autoComplete="street-address"
                  placeholder={isPickup ? t("sf.addr_ph_pickup") : payment === "etransfer" ? t("sf.addr_ph_req") : t("sf.addr_ph_opt")}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-300 text-sm font-medium resize-none focus:outline-none focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20"
                  data-testid="checkout-address-input" />
                {isPickup ? (
                  <p className="text-[11px] font-medium mt-1 px-1" style={{ color: GOLD_DARK }} data-testid="checkout-pickup-hint">
                    {t("sf.pickup_hint", { name: store.name })}
                  </p>
                ) : payment === "etransfer" && (
                  <p className="text-[11px] text-zinc-400 mt-1 px-1">{t("sf.ships_when_paid")}</p>
                )}
              </div>
            </div>

            {/* Payment selection: gold primary vs white outlined secondary */}
            <div className={`grid ${intlStore ? "grid-cols-1" : "grid-cols-2"} gap-2.5`}>
              {!intlStore && (
                <button type="button" onClick={() => setPayment("etransfer")}
                  className="rounded-xl p-3.5 text-left border-2 transition-colors"
                  style={payment === "etransfer" ? { background: GOLD, borderColor: GOLD, color: "#fff" } : { background: "#fff", borderColor: "#e4e4e7", color: "#18181b" }}
                  data-testid="checkout-pay-etransfer">
                  <Wallet size={18} className="mb-1.5" />
                  <p className="text-[13px] font-bold leading-tight">{t("sf.pay_et")}</p>
                  <p className={`text-[10px] mt-0.5 ${payment === "etransfer" ? "text-white/80" : "text-zinc-400"}`}>{t("sf.pay_et_sub")}</p>
                </button>
              )}
              <button type="button" onClick={() => setPayment("arrange")}
                className="rounded-xl p-3.5 text-left border-2 transition-colors"
                style={payment === "arrange" ? { background: GOLD, borderColor: GOLD, color: "#fff" } : { background: "#fff", borderColor: "#e4e4e7", color: "#18181b" }}
                data-testid="checkout-pay-arrange">
                <Mail size={18} className="mb-1.5" />
                <p className="text-[13px] font-bold leading-tight">{t("sf.pay_arrange")}</p>
                <p className={`text-[10px] mt-0.5 ${payment === "arrange" ? "text-white/80" : "text-zinc-400"}`}>{t("sf.pay_arrange_sub")}</p>
              </button>
            </div>

            {/* v413.2 — the distributor's own payment instructions */}
            {payment === "arrange" && store.payment_instructions && (
              <div className="rounded-xl border border-[#c9a24a]/60 bg-[#fbf6e7] p-3" data-testid="checkout-payment-instructions">
                <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: GOLD_DARK }}>{t("sf.how_to_pay")}</p>
                <p className="text-sm font-medium text-zinc-800 mt-0.5 whitespace-pre-wrap">{store.payment_instructions}</p>
              </div>
            )}

            <button type="button" onClick={placeOrder} disabled={placing || lines.length === 0}
              className="w-full h-13 py-3.5 rounded-xl text-[15px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: GOLD }}
              data-testid="checkout-place-order-btn">
              {placing ? t("sf.placing") : t("sf.place_order", { total: money(grandTotal) })}
            </button>
          </div>
          )
        ) : (
          <div className="p-6 space-y-5 text-center" data-testid="checkout-confirmation">
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center" style={{ background: GOLD_BG, border: `2px solid ${GOLD}` }}>
              <Check size={26} style={{ color: GOLD_DARK }} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-zinc-950">{t("sf.conf_received")}</h2>
              <p className="text-[13px] text-zinc-500 mt-1">{t("sf.conf_quote")}</p>
            </div>
            <button type="button"
              onClick={() => copy(result.reference, t("sf.ref_copied"), t("sf.copy_failed"))}
              className="inline-flex items-center gap-2 font-mono font-bold text-[15px] tracking-wider px-4 py-2.5 rounded-xl border-2"
              style={{ color: GOLD_DARK, borderColor: GOLD, background: GOLD_BG }}
              data-testid="checkout-reference">
              {result.reference} <Copy size={15} />
            </button>

            {address.trim() && (
              <p className="text-[12px] text-zinc-500 -mt-1" data-testid="checkout-ship-to">
                {t("sf.conf_ship_to")} <span className="font-medium text-zinc-700">{address.trim()}</span>
              </p>
            )}

            {result.payment_method === "etransfer" ? (
              <div className="text-left bg-white border-2 rounded-2xl p-4 space-y-2.5" style={{ borderColor: GOLD }} data-testid="checkout-etransfer-instructions">
                <p className="text-[12px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD_DARK }}>{t("sf.et_instructions")}</p>
                <p className="text-sm text-zinc-700 leading-relaxed">
                  {t("sf.et_send_a")} <b style={{ color: GOLD_DARK }}>{money(result.total)}</b> {t("sf.et_send_b")}
                </p>
                <button type="button" onClick={() => copy(result.etransfer_email, t("sf.email_copied"), t("sf.copy_failed"))}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-zinc-200 text-[12px] sm:text-sm font-mono font-semibold text-zinc-800 hover:border-[#C9A84C] transition-colors"
                  data-testid="checkout-copy-etransfer-email">
                  <span className="break-all text-left leading-snug">{result.etransfer_email}</span> <Copy size={14} className="shrink-0 text-zinc-400" />
                </button>
                <p className="text-[12px] text-zinc-500">{t("sf.include_ref_a")} <b>{result.reference}</b> {t("sf.include_ref_b")}</p>
                {result.customer_email && (
                  <p className="text-[12px] font-medium" style={{ color: GOLD_DARK }} data-testid="checkout-receipt-note">
                    {t("sf.receipt_to_a")} <b>{result.customer_email}</b> {t("sf.receipt_to_b")}
                  </p>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm text-zinc-600">{t("sf.arrange_note")}</p>
                {store.payment_instructions && (
                  <div className="rounded-xl border border-[#c9a24a]/60 bg-[#fbf6e7] p-3 mt-3 text-left" data-testid="result-payment-instructions">
                    <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: GOLD_DARK }}>{t("sf.how_to_pay")}</p>
                    <p className="text-sm font-medium text-zinc-800 mt-0.5 whitespace-pre-wrap">{store.payment_instructions}</p>
                  </div>
                )}
              </>
            )}

            <div className="space-y-2">
              <a href={mailtoHref}
                className="block w-full py-3.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
                style={{ background: GOLD }}
                data-testid="checkout-mailto-btn">
                {t("sf.email_order")}
              </a>
              <button type="button" onClick={onClose}
                className="block w-full py-3.5 rounded-xl text-sm font-bold border-2 border-zinc-200 text-zinc-700 hover:border-zinc-400 transition-colors"
                data-testid="checkout-done-btn">
                {t("sf.done")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
